import React, { useEffect, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

const numberFormat = new Intl.NumberFormat('es-ES');

const ChartFrame = ({ height, children, testId }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const element = containerRef.current;
    const updateSize = () => setWidth(Math.max(element.clientWidth, 0));
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full w-full min-w-0" data-testid={testId}>
      {width > 0 ? children(width, height) : <div className="h-full w-full animate-pulse rounded-[18px] bg-slate-100" />}
    </div>
  );
};

const metricCards = (analytics) => ([
  { id: 'contacts-total', label: 'Contactos importados', value: analytics?.cohorts?.contacts_total || 0, helper: 'Base consolidada del CRM' },
  { id: 'contacts-without-history', label: 'Sin historial clínico', value: analytics?.cohorts?.contacts_without_history || 0, helper: 'Requieren depuración o activación' },
  { id: 'recurrent-patients', label: 'Pacientes recurrentes', value: analytics?.cohorts?.recurrent_patients || 0, helper: 'Más de una sesión registrada' },
  { id: 'high-value-patients', label: 'Pacientes de alto valor', value: analytics?.cohorts?.high_value_patients || 0, helper: `Umbral ${numberFormat.format(analytics?.high_value_threshold || 0)}` },
]);

export const ClinicAnalyticsPanel = ({ workspace }) => {
  const analytics = workspace.analytics || {};
  const cards = metricCards(analytics);
  const campaignSegments = analytics.campaign_segments || {};

  return (
    <section className="space-y-5" data-testid="clinic-analytics-panel">
      <div className="grid gap-4 xl:grid-cols-4" data-testid="clinic-analytics-kpi-grid">
        {cards.map((card) => (
          <article key={card.id} className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid={`clinic-analytics-card-${card.id}`}>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{card.label}</p>
            <p className="mt-3 text-3xl font-black text-slate-900">{numberFormat.format(card.value)}</p>
            <p className="mt-2 text-xs text-slate-500">{card.helper}</p>
          </article>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.08fr_0.92fr]">
        <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid="clinic-analytics-monthly-chart-card">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Cohortes por periodo</p>
              <h2 className="mt-2 text-2xl font-black text-slate-900">Sesiones e ingreso histórico</h2>
            </div>
            <p className="text-xs text-slate-500" data-testid="clinic-analytics-reference-date">Referencia: {analytics.reference_date ? new Date(analytics.reference_date).toLocaleDateString('es-ES') : 'sin fecha'}</p>
          </div>
          <div className="mt-5 h-[320px] min-w-0" data-testid="clinic-analytics-monthly-chart">
            <ChartFrame height={320} testId="clinic-analytics-monthly-chart-frame">
              {(width, height) => <LineChart width={width} height={height} data={analytics.monthly_activity || []}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="sessions" fill="#0f766e" radius={[8, 8, 0, 0]} />
                <Line type="monotone" dataKey="revenue" stroke="#102a43" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>}
            </ChartFrame>
          </div>
        </article>

        <article className="rounded-[28px] border border-white/60 bg-[#102a43] p-5 text-white shadow-[0_20px_60px_rgba(16,42,67,0.28)]" data-testid="clinic-analytics-cohort-card">
          <p className="text-xs uppercase tracking-[0.24em] text-cyan-200">Cohortes listas para accionar</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              ['inactive-30', 'Inactivos 30d', analytics?.cohorts?.inactive_30 || 0],
              ['inactive-60', 'Inactivos 60d', analytics?.cohorts?.inactive_60 || 0],
              ['inactive-90', 'Inactivos 90d', analytics?.cohorts?.inactive_90 || 0],
              ['new-patients', 'Nuevos vs recurrentes', `${analytics?.cohorts?.new_patients || 0} / ${analytics?.cohorts?.recurrent_patients || 0}`],
            ].map(([id, label, value]) => (
              <div key={id} className="rounded-[22px] bg-white/10 p-4" data-testid={`clinic-analytics-cohort-${id}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{label}</p>
                <p className="mt-2 text-2xl font-black text-white">{value}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2" data-testid="clinic-analytics-actions-list">
            {(analytics.recommended_actions || []).map((item) => (
              <div key={item} className="rounded-[18px] bg-white/10 px-4 py-3 text-sm text-cyan-50">• {item}</div>
            ))}
          </div>
        </article>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_1fr]">
        <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid="clinic-analytics-labs-card">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Laboratorios / marcas</p>
              <h3 className="mt-2 text-2xl font-black text-slate-900">Ranking por stock y consumo</h3>
            </div>
          </div>
          <div className="mt-5 h-[300px] min-w-0" data-testid="clinic-analytics-labs-chart">
            <ChartFrame height={300} testId="clinic-analytics-labs-chart-frame">
              {(width, height) => <BarChart width={width} height={height} data={(analytics.top_laboratories || []).slice(0, 8)} layout="vertical" margin={{ left: 20, right: 10 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis type="category" dataKey="brand" width={88} tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="usage_units" fill="#0f766e" radius={[0, 8, 8, 0]} />
              </BarChart>}
            </ChartFrame>
          </div>
          <div className="mt-4 space-y-3" data-testid="clinic-analytics-labs-ranking">
            {(analytics.top_laboratories || []).slice(0, 5).map((lab, index) => (
              <div key={lab.brand} className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-analytics-lab-row-${index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{lab.brand}</p>
                    <p className="mt-1 text-xs text-slate-500">{lab.main_category} · stock {numberFormat.format(lab.stock_count || 0)} · consumo {numberFormat.format(lab.usage_units || 0)}</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">{lab.low_stock_items || 0} alerta(s)</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(lab.top_products || []).map((item) => (
                    <span key={`${lab.brand}-${item.label}`} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#075985]">{item.label} · {item.count}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid="clinic-analytics-products-card">
          <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Medicamentos y productos</p>
          <h3 className="mt-2 text-2xl font-black text-slate-900">Más usados en histórico</h3>
          <div className="mt-5 h-[300px] min-w-0" data-testid="clinic-analytics-products-chart">
            <ChartFrame height={300} testId="clinic-analytics-products-chart-frame">
              {(width, height) => <BarChart width={width} height={height} data={(analytics.top_products || []).slice(0, 8)}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="product_name" tick={{ fill: '#64748b', fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="usage_units" fill="#102a43" radius={[8, 8, 0, 0]} />
              </BarChart>}
            </ChartFrame>
          </div>
          <div className="mt-4 space-y-3" data-testid="clinic-analytics-products-list">
            {(analytics.high_value_patients || []).slice(0, 5).map((patient, index) => (
              <div key={`${patient.patient_name}-${index}`} className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-analytics-high-value-patient-${index}`}>
                <p className="text-sm font-bold text-slate-900">{patient.patient_name}</p>
                <p className="mt-1 text-xs text-slate-500">{patient.primary_site || 'sin sede'} · {patient.total_sessions || 0} sesiones · {numberFormat.format(patient.total_amount || 0)} histórico</p>
              </div>
            ))}
          </div>
        </article>
      </div>

      <article className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid="clinic-analytics-segments-card">
        <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Segmentos accionables</p>
        <h3 className="mt-2 text-2xl font-black text-slate-900">Listos para campañas</h3>
        <div className="mt-5 grid gap-4 xl:grid-cols-2" data-testid="clinic-analytics-segments-grid">
          {Object.entries(campaignSegments).map(([key, segment]) => (
            <div key={key} className="rounded-[24px] border border-slate-200 bg-[#f8fafc] p-4" data-testid={`clinic-analytics-segment-${key}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-900">{segment.label}</p>
                  <p className="mt-1 text-xs text-slate-500">{segment.campaign_hint}</p>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">{numberFormat.format(segment.count || 0)}</span>
              </div>
              <div className="mt-3 space-y-2" data-testid={`clinic-analytics-segment-patients-${key}`}>
                {(segment.patients || []).slice(0, 5).map((patient) => (
                  <div key={patient.patient_key} className="rounded-[18px] bg-white px-3 py-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">{patient.patient_name}</p>
                    <p className="mt-1 text-xs text-slate-500">{patient.primary_site || 'sin sede'} · {patient.total_sessions || 0} sesiones · {numberFormat.format(patient.total_amount || 0)}</p>
                    <p className="mt-1 text-xs text-slate-500">{patient.patient_phone || patient.patient_email || 'sin contacto'}</p>
                  </div>
                ))}
                {!(segment.patients || []).length && <p className="text-xs text-slate-500">Sin pacientes destacados en esta cohorte.</p>}
              </div>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
};
